import { useRef } from 'react';
import { Upload, Sparkles, Wand2, Loader2, FileText, CreditCard, CheckSquare, Square, X } from 'lucide-react';
import ListToolbar from '../../components/layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../../components/layout/ListFiltr';
import { ACCENT, OK, BAD } from '../../lib/uiTokens';
import {
    fmt, fmtDenMesic, zobrazProtistranu, STAV_LABEL, STAV_BARVA,
    KAT_LABEL, POHLEDY, pocetPohledu, FILTR_SKUPINY,
} from './bankaSdilene';

/**
 * Levý sloupec Banky — pohyby jako položky seznamu.
 *
 * Tři hustoty z jedné komponenty (norma §3 a §1c):
 *  - `siroky`  — přehled přes celou šířku: sloupcová mřížka
 *  - úzký      — vedle detailu: dvouřádková karta, nese jen to, podle čeho se vybírá
 *  - `mobilni` — blok přes celou šířku, žádný vodorovný scroll
 *
 * Řádek **nenese ovladače**. Do 3.481 v něm byla dvě ikonová tlačítka 36×36
 * a rozbalovací menu „Zařadit", které se muselo renderovat přes portál a
 * šedesáti řádky kódu se dopočítávat, aby se neořízlo o okraj okna. Klik teď
 * otevře detail a akce jsou v něm.
 *
 * **Režim výběru** (`vyberRezim`) — klik řádek zaškrtne místo otevření detailu,
 * Shift+klik označí rozsah. Spárovaný pohyb zaškrtnout nejde: hromadné akce
 * mění stav a zařazení, doklady se odpojují jen vědomě v detailu.
 * Lištu hromadných akcí kreslí rodič (`listaDole`), pod seznamem.
 */

const GRID = '86px minmax(0,1fr) 138px 186px 124px 112px';
const GRID_VYBER = `36px ${GRID}`;

/** Pohyb, který jde hromadně měnit — spárovaný ne (má doklady). */
export const lzeVybrat = (t) => t.stav_parovani !== 'paroveno';

function Zaskrtnuti({ zapnuto, zakazano }) {
    const Ikona = zapnuto ? CheckSquare : Square;
    return <Ikona size={17} style={{ color: zapnuto ? ACCENT : 'var(--text-tertiary)', opacity: zakazano ? 0.35 : 1, flexShrink: 0 }} />;
}

function StavBadge({ stav }) {
    const barva = STAV_BARVA[stav] || 'var(--text-tertiary)';
    return (
        <span style={{
            fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            padding: '3px 7px', borderRadius: 5, color: barva, background: `${barva}1f`, whiteSpace: 'nowrap',
        }}>{STAV_LABEL[stav] || stav}</span>
    );
}

const Castka = ({ smer, castka, size = 14 }) => (
    <span className="tabular-nums" style={{
        color: smer === 'prichozi' ? OK : BAD, fontWeight: 700, fontSize: size, whiteSpace: 'nowrap',
    }}>{smer === 'prichozi' ? '+' : '−'}{fmt(castka)}</span>
);

/** Popisek dokladu u pohybu — víc dokladů na jednu platbu je běžné (alokace). */
const dokladLabel = (t) => {
    const d = t.doklady || [];
    if (!d.length) return null;
    if (d.length === 1) return `${d[0].interni_cislo || `#${d[0].id}`}${d[0].dodavatel ? ` · ${d[0].dodavatel}` : ''}`;
    return `${d.length} doklady`;
};

export default function PohybyListPanel({
    siroky = false, mobilni = false, isDark, mutedColor, inputStyle,
    transakce = [], loading = false, staty = null,
    pohled, onPohled, hledani, filtrHodnoty, onFiltr, onResetFiltru,
    vybranyId = null, onVyber,
    onNahrat, onProjit, onPravidla, onSparujKartove, nahravam = false, projizdim = false, parujiKarty = false,
    rezervaDole = null,
    vyberRezim = false, vybrane = null, onOznac, onPrepniRezim, listaDole = null,
}) {
    const posledniKlik = useRef(null);
    const jeVybrany = (t) => !!vybrane?.has(t.id);

    // V režimu výběru klik zaškrtává; Shift+klik dooznačí rozsah od posledního.
    const klik = (t, e) => {
        if (!vyberRezim) { onVyber?.(t.id); return; }
        if (!lzeVybrat(t)) return;
        if (e?.shiftKey && posledniKlik.current != null) {
            const a = transakce.findIndex(x => x.id === posledniKlik.current);
            const b = transakce.findIndex(x => x.id === t.id);
            if (a >= 0 && b >= 0) {
                const rozsah = transakce.slice(Math.min(a, b), Math.max(a, b) + 1).filter(lzeVybrat).map(x => x.id);
                onOznac?.(rozsah, true);
                posledniKlik.current = t.id;
                return;
            }
        }
        onOznac?.([t.id], !jeVybrany(t));
        posledniKlik.current = t.id;
    };

    const pohledy = POHLEDY.map(p => ({ ...p, pocet: pocetPohledu(p, staty) }));

    const toolbar = (
        <ListToolbar
            siroky={siroky} kompaktni={mobilni}
            isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle} barva={ACCENT}
            hledani={{
                value: hledani.value, onChange: hledani.onChange,
                placeholder: 'Hledat protistranu, VS nebo popis…', placeholderKratky: 'Hledat…',
            }}
            akce={[
                vyberRezim
                    ? { id: 'vybrat', label: 'Ukončit výběr', Icon: X, onClick: onPrepniRezim, title: 'Zpět na běžné procházení' }
                    : { id: 'vybrat', label: 'Vybrat více', Icon: CheckSquare, onClick: onPrepniRezim, title: 'Označit víc pohybů a zařadit je najednou' },
                { id: 'projit', label: 'Projít pohyby', Icon: projizdim ? Loader2 : Sparkles, onClick: onProjit, title: 'Projít nespárované a navrhnout párování i zařazení' },
                { id: 'nahrat', label: 'Nahrát výpis', Icon: nahravam ? Loader2 : Upload, onClick: onNahrat, primarni: true },
            ]}
            nastrojeAkce={[
                { id: 'karty', label: 'Spárovat jisté kartové', Icon: parujiKarty ? Loader2 : CreditCard, onClick: onSparujKartove, title: 'Dodatečně spárovat platby kartou, kde sedí den nákupu, částka a karta' },
                { id: 'pravidla', label: 'AI pravidla', Icon: Wand2, onClick: onPravidla },
            ]}
            filtr={(
                <FiltrTlacitko
                    skupiny={FILTR_SKUPINY} hodnoty={filtrHodnoty}
                    onChange={onFiltr} onReset={onResetFiltru}
                    pocetVysledku={transakce.length}
                    siroky={siroky} kompaktni={mobilni}
                    isDark={isDark} mutedColor={mutedColor} barva={ACCENT}
                />
            )}
            pohledy={pohledy}
            pohledAktivni={pohled}
            onPohled={onPohled}
            aktivniFiltry={(
                <AktivniFiltry skupiny={FILTR_SKUPINY} hodnoty={filtrHodnoty}
                    onChange={onFiltr} onReset={onResetFiltru}
                    isDark={isDark} mutedColor={mutedColor} barva={ACCENT} />
            )}
        />
    );

    const prazdno = (
        <div className="text-center py-16 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            {loading ? null : 'Žádné pohyby. Nahraj výpis tlačítkem nahoře.'}
        </div>
    );

    // ── Mobil: bloky přes celou šířku ───────────────────────────────────────
    if (mobilni) {
        return (
            <div className="flex flex-col flex-1 min-h-0 min-w-0">
                <div className="flex-shrink-0 px-4 pt-3">{toolbar}</div>
                <div className="flex-1 min-h-0 overflow-y-auto px-4" style={{ paddingBottom: rezervaDole || 16 }}>
                    {loading && <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin" style={{ color: ACCENT }} /></div>}
                    {!loading && transakce.length === 0 && prazdno}
                    {transakce.map(t => (
                        <button key={t.id} onClick={(e) => klik(t, e)}
                            className="w-full text-left flex items-center gap-3"
                            style={{ padding: '11px 0 10px', minHeight: 56, borderBottom: '1px solid var(--border-default)', background: 'none' }}>
                            {vyberRezim && <Zaskrtnuti zapnuto={jeVybrany(t)} zakazano={!lzeVybrat(t)} />}
                            <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="text-[13.5px] font-bold truncate">{zobrazProtistranu(t)}</span>
                                <Castka smer={t.smer} castka={t.castka} size={17} />
                            </div>
                            <div className="text-[11px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                                {fmtDenMesic(t.datum)}
                                {t.variabilni_symbol ? ` · VS ${t.variabilni_symbol}` : ''}
                                {t.tag ? ` · ${KAT_LABEL[t.tag] || t.tag}` : ''}
                                {' · '}
                                <span style={{ color: STAV_BARVA[t.stav_parovani] }}>{STAV_LABEL[t.stav_parovani]}</span>
                            </div>
                            </div>
                        </button>
                    ))}
                </div>
                {listaDole && <div className="flex-shrink-0 px-4 pt-2" style={{ paddingBottom: rezervaDole || 12 }}>{listaDole}</div>}
            </div>
        );
    }

    // ── Desktop ─────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col flex-1 min-h-0 min-w-0">
            <div className="flex-shrink-0 mb-2.5">{toolbar}</div>
            <div className="flex-1 min-h-0 rounded-2xl overflow-hidden flex flex-col"
                style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)' }}>

                {siroky && (
                    <div style={{
                        display: 'grid', gridTemplateColumns: vyberRezim ? GRID_VYBER : GRID, alignItems: 'center', height: 34, flexShrink: 0,
                        background: 'var(--surface-elevated)', borderBottom: '1px solid var(--border-default)',
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)',
                    }}>
                        {vyberRezim && <div />}
                        <div style={{ paddingLeft: vyberRezim ? 0 : 14 }}>Datum</div>
                        <div>Protistrana</div>
                        <div>Zařazení</div>
                        <div>Doklad</div>
                        <div style={{ textAlign: 'right', paddingRight: 14 }}>Částka</div>
                        <div style={{ textAlign: 'right', paddingRight: 14 }}>Stav</div>
                    </div>
                )}

                <div className="flex-1 min-h-0 overflow-y-auto">
                    {loading && <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin" style={{ color: ACCENT }} /></div>}
                    {!loading && transakce.length === 0 && prazdno}

                    {!loading && transakce.map(t => {
                        const vybrany = vyberRezim ? jeVybrany(t) : t.id === vybranyId;
                        const doklad = dokladLabel(t);

                        if (siroky) {
                            return (
                                <div key={t.id} onClick={(e) => klik(t, e)} role="button" tabIndex={0}
                                    onKeyDown={e => { if (e.key === 'Enter' || (vyberRezim && e.key === ' ')) { e.preventDefault(); klik(t, e); } }}
                                    style={{
                                        display: 'grid', gridTemplateColumns: vyberRezim ? GRID_VYBER : GRID, alignItems: 'center', height: 46,
                                        borderBottom: '1px solid var(--border-subtle)', cursor: vyberRezim && !lzeVybrat(t) ? 'default' : 'pointer',
                                        userSelect: vyberRezim ? 'none' : undefined,
                                        background: vybrany ? 'rgba(255,255,255,0.05)' : 'transparent',
                                        borderLeft: vybrany ? `3px solid ${ACCENT}` : '3px solid transparent',
                                    }}>
                                    {vyberRezim && <div style={{ paddingLeft: 11 }}><Zaskrtnuti zapnuto={vybrany} zakazano={!lzeVybrat(t)} /></div>}
                                    <div style={{ paddingLeft: vyberRezim ? 0 : 11, fontSize: 12.5, color: 'var(--text-secondary)' }}>{fmtDenMesic(t.datum)}</div>
                                    <div style={{ minWidth: 0, paddingRight: 10 }}>
                                        <div className="text-[13.5px] font-semibold truncate">{zobrazProtistranu(t)}</div>
                                        <div className="text-[10.5px] truncate" style={{ color: 'var(--text-secondary)' }}>
                                            {t.variabilni_symbol ? `VS ${t.variabilni_symbol} · ` : ''}{t.popis || '—'}
                                        </div>
                                    </div>
                                    <div className="text-[11.5px] truncate" style={{ paddingRight: 10, color: t.tag ? 'var(--text-secondary)' : 'var(--text-tertiary)' }}>
                                        {t.tag ? (KAT_LABEL[t.tag] || t.tag) : '—'}
                                    </div>
                                    <div className="text-[12px] truncate" style={{ paddingRight: 10, color: doklad ? ACCENT : 'var(--text-tertiary)' }}>
                                        {doklad ? <span className="inline-flex items-center gap-1"><FileText size={11} />{doklad}</span> : '—'}
                                    </div>
                                    <div style={{ textAlign: 'right', paddingRight: 14 }}><Castka smer={t.smer} castka={t.castka} /></div>
                                    <div style={{ textAlign: 'right', paddingRight: 14 }}><StavBadge stav={t.stav_parovani} /></div>
                                </div>
                            );
                        }

                        // Úzký sloupec vedle detailu — karta, nese jen to, podle čeho se vybírá.
                        return (
                            <div key={t.id} onClick={(e) => klik(t, e)} role="button" tabIndex={0}
                                onKeyDown={e => { if (e.key === 'Enter') klik(t, e); }}
                                className={vyberRezim ? 'flex items-center gap-2.5' : undefined}
                                style={{
                                    padding: '10px 12px', cursor: 'pointer', userSelect: vyberRezim ? 'none' : undefined,
                                    borderBottom: '1px solid var(--border-subtle)',
                                    background: vybrany ? 'rgba(255,255,255,0.05)' : 'transparent',
                                    borderLeft: vybrany ? `3px solid ${ACCENT}` : '3px solid transparent',
                                }}>
                                {vyberRezim && <Zaskrtnuti zapnuto={vybrany} zakazano={!lzeVybrat(t)} />}
                                <div className={vyberRezim ? 'flex-1 min-w-0' : undefined}>
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[12.5px] font-semibold truncate">{zobrazProtistranu(t)}</span>
                                    <Castka smer={t.smer} castka={t.castka} size={13} />
                                </div>
                                <div className="text-[10.5px] mt-0.5 truncate" style={{ color: 'var(--text-secondary)' }}>
                                    {fmtDenMesic(t.datum)}
                                    {t.variabilni_symbol ? ` · VS ${t.variabilni_symbol}` : ''}
                                    {' · '}
                                    <span style={{ color: STAV_BARVA[t.stav_parovani] }}>{STAV_LABEL[t.stav_parovani]}</span>
                                </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {listaDole && <div className="flex-shrink-0">{listaDole}</div>}
            </div>
        </div>
    );
}
